import { TAGButton } from '@aspendental/shared-components-web';
import { WarningAmber } from '@mui/icons-material';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { usePortalSessionManager } from '@src/hooks/usePortalSessionManager';
import { useCallback, useEffect, useRef, useState } from 'react';
import { warningCardActionBar, warningCardContent, warningCardText, warningCardTextContainer, warningIcon, warningOverlay } from './SessionTimeoutWarning.styles';
interface SessionTimeoutWarningProps {
  readonly sessionManager: ReturnType<typeof usePortalSessionManager>;
}
export default function SessionTimeoutWarning({
  sessionManager
}: SessionTimeoutWarningProps) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [timeLeft, setTimeLeft] = useState(sessionManager.sessionWarning.timeLeft);
  const endTimeRef = useRef<number | null>(null);
  const handleLogout = useCallback(async () => {
    sessionManager.triggerLogout();
  }, [sessionManager]);
  const handleContinueSession = async () => {
    const result = await sessionManager.extendSession();
    if (result.triggerLogout) {
      handleLogout();
    } else {
      sessionManager.hideSessionWarning();
    }
  };
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  useEffect(() => {
    if (!endTimeRef.current) {
      endTimeRef.current = Date.now() + timeLeft * 1000;
    }
    const timer = setInterval(() => {
      const msLeft = endTimeRef.current! - Date.now();
      const nextTimeLeft = Math.max(0, Math.ceil(msLeft / 1000));
      setTimeLeft(nextTimeLeft);
      if (nextTimeLeft === 0) {
        handleLogout();
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [handleLogout, timeLeft]);
  return <Box sx={warningOverlay} data-sentry-element="Box" data-sentry-component="SessionTimeoutWarning" data-sentry-source-file="SessionTimeoutWarning.tsx">
			<Box sx={warningCardContent} data-sentry-element="Box" data-sentry-source-file="SessionTimeoutWarning.tsx">
				<Box sx={warningCardTextContainer} data-sentry-element="Box" data-sentry-source-file="SessionTimeoutWarning.tsx">
					<WarningAmber sx={warningIcon} data-sentry-element="WarningAmber" data-sentry-source-file="SessionTimeoutWarning.tsx" />
					<Box sx={warningCardText} data-sentry-element="Box" data-sentry-source-file="SessionTimeoutWarning.tsx">
						<Typography variant="header3" data-sentry-element="Typography" data-sentry-source-file="SessionTimeoutWarning.tsx">Your session is about to expire</Typography>
						<Typography variant="bodyMediumBook" data-sentry-element="Typography" data-sentry-source-file="SessionTimeoutWarning.tsx">
							You will be automatically signed out in
							<Typography variant="bodyMediumBold" data-sentry-element="Typography" data-sentry-source-file="SessionTimeoutWarning.tsx"> {formatTime(timeLeft)}</Typography>
						</Typography>
					</Box>
				</Box>
				<Box sx={warningCardActionBar(isLargeScreen)} data-sentry-element="Box" data-sentry-source-file="SessionTimeoutWarning.tsx">
					<TAGButton size="M" type="button" variant="tertiaryDefault" onClick={handleContinueSession} data-sentry-element="TAGButton" data-sentry-source-file="SessionTimeoutWarning.tsx">
						Continue session
					</TAGButton>
					<TAGButton size="M" type="button" variant="tertiaryDefault" onClick={handleLogout} data-sentry-element="TAGButton" data-sentry-source-file="SessionTimeoutWarning.tsx">
						Sign out
					</TAGButton>
				</Box>
			</Box>
		</Box>;
}